import classNames from 'classnames';
import { Category } from '../../../../../clients/quipli-service/category/category';
import { Product } from '../../../../../clients/quipli-service/product/product';
import { Card } from '../../../../../components/card/card';
import { DistinctSelect } from '../../../../../components/distinct-select/distinct-select';
import { SelectForm } from '../../../../../components/select-form/select-form';
import { Typography } from '../../../../../components/typography/typography';

export const EligibleProductsAndCategories = ({
  productAndCategorySpecific,
  setProductAndCategorySpecific,
  products,
  setProducts,
  categories,
  setCategories,
  allProducts,
  allCategories,
  isLoadingOrSaving,
  save,
}: {
  productAndCategorySpecific: boolean;
  products: number[];
  categories: number[];
  setProductAndCategorySpecific: (value: boolean) => void;
  setProducts: (value: number[]) => void;
  setCategories: (value: number[]) => void;
  allProducts: Product[];
  allCategories: Category[];
  isLoadingOrSaving: boolean;
  save: (values?: { productAndCategorySpecific?: boolean; products?: number[]; categories?: number[] }) => void;
}) => {
  const options = [
    {
      label: 'All Products',
      value: <Typography type="text-sm">All Products</Typography>,
      isSelected: productAndCategorySpecific === false,
    },
    {
      label: 'Selected products and categories',
      value: <Typography type="text-sm">Selected Products and Categories</Typography>,
      isSelected: productAndCategorySpecific === true,
    },
  ];
  return (
    <Card className="card flex flex-col gap-4">
      <div>
        <Typography type="text-xl" weight="semi-bold">
          Eligible Products and Categories
        </Typography>
        <Typography type="text-md" weight="regular" className="text-grey-500">
          Apply this discount to all products, or limit it to selected products or categories.{' '}
        </Typography>
      </div>
      <DistinctSelect
        options={options}
        onChange={(selected) => {
          if (selected.label === 'All Products') {
            setProductAndCategorySpecific(false);
            setProducts([]);
            setCategories([]);
            save({ productAndCategorySpecific: false, products: [], categories: [] });
          } else {
            setProductAndCategorySpecific(true);
            save({ productAndCategorySpecific: true, products: [], categories: [] });
          }
        }}
      />
      {/* If all products are selected, make this slightly grey and not clickable, and add hover to clarify */}
      <div
        className={classNames(!productAndCategorySpecific ? 'opacity-50' : '', 'gap-2 flex flex-col')}
        title={
          !productAndCategorySpecific ? 'All products are selected, so no products or categories can be selected' : ''
        }
        data-cy="eligible-products-and-categories"
      >
        <EligibleProducts
          productAndCategorySpecific={productAndCategorySpecific}
          products={products}
          setProducts={setProducts}
          allProducts={allProducts}
          isLoadingOrSaving={isLoadingOrSaving}
          save={save}
        />
        <EligibleCategories
          productAndCategorySpecific={productAndCategorySpecific}
          categories={categories}
          setCategories={setCategories}
          allCategories={allCategories}
          isLoadingOrSaving={isLoadingOrSaving}
          save={save}
        />
      </div>
    </Card>
  );
};

const EligibleProducts = ({
  productAndCategorySpecific,
  products,
  setProducts,
  save,
  allProducts,
  isLoadingOrSaving,
}: {
  productAndCategorySpecific: boolean;
  products: number[];
  setProducts: (value: number[]) => void;
  save: (values?: { products?: number[] }) => void;
  allProducts: Product[];
  isLoadingOrSaving: boolean;
}) => {
  const productToOptions = (product: Product) => ({ value: product.id, label: product.name });

  return (
    <div className="flex flex-col gap-2">
      <Typography className="flex flex-row items-center" type="text-md" weight="regular">
        Eligible Products
      </Typography>
      <SelectForm
        options={allProducts.map(productToOptions)}
        value={allProducts.filter((product) => products.includes(product.id)).map(productToOptions)}
        onChange={(selected) => {
          const selectedIds = selected.map((s) => s.value);
          setProducts(selectedIds);
          save({ products: selectedIds });
        }}
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        isDisabled={!productAndCategorySpecific}
        isLoading={isLoadingOrSaving}
        menuShouldBlockScroll={true}
        menuPosition="fixed"
        menuPortalTarget={document.body}
      />
    </div>
  );
};

const EligibleCategories = ({
  productAndCategorySpecific,
  categories,
  setCategories,
  allCategories,
  isLoadingOrSaving,
  save,
}: {
  productAndCategorySpecific: boolean;
  categories: number[];
  setCategories: (value: number[]) => void;
  allCategories: Category[];
  isLoadingOrSaving: boolean;
  save: (values?: { categories?: number[] }) => void;
}) => {
  const categoryToOptions = (category: Category) => ({ value: category.id, label: category.name });

  return (
    <div className="flex flex-col gap-2">
      <Typography className="flex flex-row items-center" type="text-md" weight="regular">
        Eligible Categories
      </Typography>
      <SelectForm
        options={allCategories.map(categoryToOptions)}
        value={allCategories.filter((category) => categories.includes(category.id)).map(categoryToOptions)}
        onChange={(selected) => {
          const selectedIds = selected.map((s) => s.value);
          setCategories(selectedIds);
          save({ categories: selectedIds });
        }}
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        isDisabled={!productAndCategorySpecific}
        isLoading={isLoadingOrSaving}
        menuShouldBlockScroll={true}
        menuPosition="fixed"
        menuPortalTarget={document.body}
      />
    </div>
  );
};
